import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import IntegrationHero from '../../components/integration-hero'
import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import { getCanonicalUrl } from '../../utils/canonical'
import DarkNav from '../../components/dark-nav'
import './power-automate.css'

const PowerAutomateIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    // Initialize AOS animations if available
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      });
    }

    // Show scroll-to-top button when page is scrolled down
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Breadcrumb navigation
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Microsoft Power Automate' }
  ]

  // Hero section content
  const heroContent = {
    badge: "Enterprise Integration",
    title: "DataFlows SMS for Microsoft Power Automate",
    subtitle: "Transform your business communications with enterprise-grade SMS messaging directly within your Power Automate flows and Power Apps.",
    description: "Automate SMS workflows, create intelligent responses, and integrate SMS capabilities seamlessly into your existing Microsoft Power Platform ecosystem.",
    primaryCta: {
      text: "Get Started with Power Automate",
      href: "https://make.powerautomate.com/"
    },
    secondaryCta: {
      text: "Contact Sales",
      href: "/contact-us"
    },
    features: [
      "Easy connector installation",
      "Two-way SMS automation",
      "Dynamic sender ID selection",
      "Real-time flow triggers"
    ]
  }

  // Main features
  const features = [
    {
      icon: "⚡",
      title: "Automated SMS Workflows",
      description: "Create intelligent SMS flows that respond to business events, customer actions, and system triggers automatically."
    },
    {
      icon: "🔄",
      title: "Two-Way Conversations",
      description: "Build interactive SMS workflows with automatic responses, keyword matching, and conversation routing."
    },
    {
      icon: "🎯",
      title: "Smart Sender ID Management",
      description: "Automatically select appropriate sender IDs based on business rules, customer segments, or message types."
    },
    {
      icon: "🏢",
      title: "Enterprise Integration",
      description: "Connect with CRM systems, databases, and other Microsoft services for comprehensive business automation."
    }
  ]

  return (
    <div className="power-automate-container">
      <DarkNav />
      <Helmet>
        <title>DataFlows SMS for Microsoft Power Automate — Enterprise SMS Automation</title>
        <link rel="canonical" href={getCanonicalUrl('/integrations/power-automate')} />
        <meta
          name="description"
          content="Integrate DataFlows SMS with Microsoft Power Automate for enterprise SMS automation. Create intelligent workflows, automate responses, and connect with your business systems."
        />
        <meta name="keywords" content="Power Automate SMS, Microsoft automation, enterprise SMS, workflow automation, business SMS integration" />
        <meta property="og:title" content="DataFlows SMS for Microsoft Power Automate Integration" />
        <meta property="og:description" content="Transform business communications with enterprise SMS automation in Microsoft Power Automate. Create intelligent workflows and automate customer interactions." />
        <meta property="og:image" content="https://dataflows.com.au/logos/lglogopowerautomate-200h.png" />
        <meta property="og:url" content="https://dataflows.com.au/integrations/power-automate" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/power-automate')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Microsoft Power Automate", "item": "https://dataflows.com.au/integrations/power-automate" }
          ]
        })}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="power-automate-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <IntegrationHero {...heroContent} />

      {/* Page Navigation */}
      <section className="power-automate-navigation">
        <div className="container">
          <div className="section-title" data-aos="fade-up">
            <h2>Complete Power Automate Documentation</h2>
            <p className="section-subtitle">
              Explore our comprehensive guides to master SMS automation with Microsoft Power Automate
            </p>
          </div>

          <div className="nav-cards-grid">
            <Link to="/integrations/power-automate" className="nav-page-card current" data-aos="fade-up">
              <div className="nav-card-icon">📋</div>
              <h3>Setup Guide</h3>
              <p>Complete installation and configuration guide for the DataFlows SMS connector</p>
              <span className="nav-card-badge">Current Page</span>
            </Link>

            <Link to="/integrations/power-automate/advanced" className="nav-page-card" data-aos="fade-up" data-aos-delay="100">
              <div className="nav-card-icon">⚡</div>
              <h3>Advanced Features</h3>
              <p>Webhook triggers, enterprise integrations, and advanced automation patterns</p>
              <span className="nav-card-arrow">→</span>
            </Link>

            <Link to="/integrations/power-automate/use-cases" className="nav-page-card" data-aos="fade-up" data-aos-delay="200">
              <div className="nav-card-icon">💼</div>
              <h3>Use Cases</h3>
              <p>Real-world examples and industry-specific SMS automation implementations</p>
              <span className="nav-card-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="power-automate-features">
        <div className="container">
          <div className="section-title" data-aos="fade-up">
            <h2>Enterprise SMS Automation</h2>
            <p className="section-subtitle">
              Everything you need to automate SMS communications within your Microsoft Power Platform ecosystem
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div
                  className="feature-icon emoji-icon"
                  style={{
                    fontSize: '48px',
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Setup Guide Section */}
      <section className="power-automate-guide">
        <div className="container">
          <div className="guide-content" data-aos="fade-up">
            <h2>DataFlows SMS Connector - Quick Start Guide</h2>

            <div className="prerequisites">
              <h4>Prerequisites</h4>
              <ul>
                <li>A Power Automate licence that includes Premium connectors</li>
                <li>DataFlows SMS account with API access</li>
                <li>Virtual phone numbers assigned to your account</li>
              </ul>
            </div>

            <h3>Getting Started</h3>

            <p>DataFlows SMS is a certified Microsoft connector, listed as <a href="https://learn.microsoft.com/en-us/connectors/dataflowssms/" target="_blank" rel="noreferrer noopener">DataFlows SMS (Preview)</a>, a Premium connector. There is nothing to install.</p>

            <h4><span className="step-number">1</span>Add the Connector to a Flow</h4>
            <ol>
              <li><strong>Open Power Automate</strong> (make.powerautomate.com)</li>
              <li><strong>Create or open</strong> a flow and add an action</li>
              <li><strong>Search for</strong> "DataFlows SMS"</li>
            </ol>

            <h4><span className="step-number">2</span>Create Your First Connection</h4>
            <ol>
              <li><strong>Click</strong> "Create connection"</li>
              <li><strong>Enter your DataFlows API key</strong>
                <ul>
                  <li>Copy it from the Developers page in your DataFlows account</li>
                  <li>Format: <code>34|aBc123xyz...</code></li>
                </ul>
              </li>
              <li><strong>Test connection</strong> - should show "Connected successfully"</li>
            </ol>

            <h3>Your First SMS Flow</h3>

            <h4>Create an Automated SMS Response</h4>
            <p><strong>Scenario:</strong> Send an automatic reply when someone texts your business number.</p>

            <h4>Flow Setup:</h4>
            <ol>
              <li><strong>Create new flow</strong> → "Automated cloud flow"</li>
              <li><strong>Add trigger:</strong> "When SMS received"
                <ul>
                  <li><strong>Event Type:</strong> <code>sms_received</code></li>
                  <li><strong>Virtual Number:</strong> Select your business number</li>
                </ul>
              </li>
              <li><strong>Add action:</strong> "Send SMS Message"
                <ul>
                  <li><strong>Recipient:</strong> Use dynamic content → "Event Data Sender Phone"</li>
                  <li><strong>Sender ID:</strong> Select your preferred sender ID</li>
                  <li><strong>Message:</strong> "Thank you for contacting us! We'll respond within 24 hours."</li>
                </ul>
              </li>
              <li><strong>Save and test</strong> your flow</li>
            </ol>

            <h4>Test Your Flow</h4>
            <ol>
              <li><strong>Send an SMS</strong> to your virtual number</li>
              <li><strong>Check Flow run history</strong> - should show "Succeeded"</li>
              <li><strong>Verify</strong> automatic reply was sent</li>
            </ol>

            <h3>Common Configurations</h3>

            <h4>Dynamic Sender ID Selection</h4>
            <p>The connector automatically loads your available sender IDs:</p>
            <ul>
              <li>Approved alphanumeric sender IDs</li>
              <li>Your virtual phone numbers</li>
              <li>Custom branded sender names</li>
            </ul>

            <h4>Virtual Number Filtering</h4>
            <p>Target specific business lines:</p>
            <ul>
              <li><strong>Customer Service:</strong> <code>614095xxxxx</code></li>
              <li><strong>Marketing:</strong> <code>614001xxxxx</code></li>
              <li><strong>Support:</strong> <code>614098xxxxx</code></li>
            </ul>

            <h4>Message Types Supported</h4>
            <ul>
              <li><strong>Plain Text:</strong> Standard SMS messages</li>
              <li><strong>Unicode:</strong> International characters and emojis</li>
              <li><strong>Long Messages:</strong> Automatically split across multiple SMS</li>
            </ul>

            <div className="troubleshooting">
              <h4>Troubleshooting</h4>

              <h4>Common Issues</h4>

              <p><strong>"Invalid API Token"</strong></p>
              <ul>
                <li>Verify token format: <code>34|aBc123xyz...</code></li>
                <li>Check token hasn't expired in DataFlows account</li>
                <li>Ensure Developer permissions are enabled</li>
              </ul>

              <p><strong>"No Virtual Numbers Found"</strong></p>
              <ul>
                <li>Confirm numbers are assigned in DataFlows account</li>
                <li>Check number status is "Active"</li>
                <li>Contact support if numbers don't appear</li>
              </ul>

              <p><strong>"SMS Not Sending"</strong></p>
              <ul>
                <li>Verify sender ID is approved</li>
                <li>Check account balance</li>
                <li>Ensure recipient number format is correct (include country code)</li>
              </ul>
            </div>

            <h3>Next Steps</h3>

            <h4>Explore Advanced Features</h4>
            <ol>
              <li><strong>Business Automation Triggers</strong>
                <ul>
                  <li>Balance low alerts</li>
                  <li>Campaign completion notifications</li>
                  <li>Keyword matching responses</li>
                </ul>
              </li>
              <li><strong>Two-Way Conversations</strong>
                <ul>
                  <li>Build interactive SMS workflows</li>
                  <li>Customer service automation</li>
                  <li>Order status systems</li>
                </ul>
              </li>
              <li><strong>Integration Examples</strong>
                <ul>
                  <li>Connect with CRM systems</li>
                  <li>E-commerce order notifications</li>
                  <li>Appointment reminders</li>
                </ul>
              </li>
            </ol>

            <h4>Pre-Built Templates</h4>
            <p>Download ready-to-use flow templates:</p>
            <ul>
              <li>Customer service automation</li>
              <li>Marketing campaign responses</li>
              <li>Emergency notification systems</li>
              <li>Order confirmation workflows</li>
            </ul>

            <div className="support-links">
              <h4>Support & Resources</h4>
              <p><strong>Need Help?</strong></p>
              <ul>
                <li>Email: <a href="mailto:support@dataflows.com.au">support@dataflows.com.au</a></li>
                <li>Support Request: <a href="/contact-us">https://dataflows.com.au/contact-us</a></li>
              </ul>

              <p><strong>Feature Requests</strong></p>
              <ul>
                <li>Submit ideas through DataFlows dashboard</li>
                <li>Join our Power Platform community</li>
                <li>Follow product updates</li>
              </ul>
            </div>

            <p style={{textAlign: 'center', fontStyle: 'italic', color: '#6c757d', marginTop: '2rem'}}>
              <em>DataFlows SMS Connector v1.0 - Enterprise SMS for Power Platform</em>
            </p>
          </div>
        </div>
      </section>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-top-btn"
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            backgroundColor: '#5228F5',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        >
          ↑
        </button>
      )}

      <Footer />
    </div>
  )
}

export default PowerAutomateIntegration